import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Animation } from "@babylonjs/core/Animations/animation";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";

// ゲーム開始時のカメラ登場演出：
// カメラがキャラクター周りを 1 周しながら接近 → 元の位置に戻る（映画的）。
// 約 3.5 秒、初回ロード時のみ呼ぶ。speedRatio 引数は持たず、ゲームの倍速設定の影響を受けない。
//
// frameRate=30, totalFrames=105 → 105/30 = 3.5 秒
// keyframes:
//   0   (0.0s) 初期位置（俯瞰）
//   35  (1.2s) +π/2 回転、半接近（initialRadius * 0.6）、target 半分キャラへ
//   70  (2.3s) +3π/2 回転、最接近（radius=6）、target=キャラ位置、beta やや見下ろし
//   105 (3.5s) +2π = 初期角度に復帰、初期 radius/target/beta
const FRAME_RATE = 30;
const TOTAL_FRAMES = 105;

export const performCameraIntro = (
  scene: Scene,
  camera: ArcRotateCamera,
  charPos: Vector3,
  initial: { alpha: number; beta: number; radius: number; target: Vector3 },
): Promise<void> =>
  new Promise<void>((resolve) => {
    if (!scene || scene.isDisposed) return resolve();

    const midTarget = Vector3.Lerp(initial.target, charPos, 0.5);
    const closeRadius = 6;
    const tiltedBeta = Math.min(initial.beta * 1.2, Math.PI / 2 - 0.05);

    const animAlpha = new Animation(
      "camIntroAlpha",
      "alpha",
      FRAME_RATE,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    animAlpha.setKeys([
      { frame: 0, value: initial.alpha },
      { frame: 35, value: initial.alpha + Math.PI / 2 },
      { frame: 70, value: initial.alpha + (3 * Math.PI) / 2 },
      { frame: TOTAL_FRAMES, value: initial.alpha + 2 * Math.PI },
    ]);

    const animBeta = new Animation(
      "camIntroBeta",
      "beta",
      FRAME_RATE,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    animBeta.setKeys([
      { frame: 0, value: initial.beta },
      { frame: 70, value: tiltedBeta },
      { frame: TOTAL_FRAMES, value: initial.beta },
    ]);

    const animRadius = new Animation(
      "camIntroRadius",
      "radius",
      FRAME_RATE,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    animRadius.setKeys([
      { frame: 0, value: initial.radius },
      { frame: 35, value: initial.radius * 0.6 },
      { frame: 70, value: closeRadius },
      { frame: TOTAL_FRAMES, value: initial.radius },
    ]);

    const animTarget = new Animation(
      "camIntroTarget",
      "target",
      FRAME_RATE,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    animTarget.setKeys([
      { frame: 0, value: initial.target.clone() },
      { frame: 35, value: midTarget.clone() },
      { frame: 70, value: charPos.clone() },
      { frame: TOTAL_FRAMES, value: initial.target.clone() },
    ]);

    const a = scene.beginDirectAnimation(
      camera,
      [animAlpha, animBeta, animRadius, animTarget],
      0,
      TOTAL_FRAMES,
      false,
      1,
    );
    a.onAnimationEnd = () => resolve();
  });
